import { Colors, Services } from '@sigma-frontend-libs/commons'

import { buttonModes, components, icons, labelModes, sizes } from '@/utils'

import button from '@/components/button'
import info from '@/components/info'
import preloader from '@/components/preloader'
import tag from '@/components/tag'
import overflow from '@/components/text/overflow'
import importCSVButton from '@/components/trees/importCSVButton'

function renderPreloader(h) {
  return h(
    'div',
    {
      class: 'tree-preloader'
    },
    [ h(preloader) ]
  )
}

function renderError(h) {
  return h(
    info,
    {
      props: {
        value: this.getTranslate(`${Services.operatorGroups}.errors.mccmnc.dictionary`),
        color: Colors.error
      }
    }
  )
}

function renderSearchField(h) {
  return h(
    components['text-field'],
    {
      props: {
        value: this.searchValue,
        label: this.getTranslate('commons.labels.search'),
        afterIcon: icons.search,
        labelMode: labelModes.hidden,
        loading: this.loading.search,
        dense: true,
        clearable: true,
        details: false
      },
      on: {
        input: event => {
          this.searchValue = event
        }
      }
    }
  )
}
function renderImportButton(h) {
  return h(
    importCSVButton,
    {
      props: {
        value: this.proxy,
        parser: this.parser,
        tooltip: this.getTranslate('misc.hints.countriesImportCSV')
      },
      on: {
        input: event => {
          this.proxy = event
        }
      }
    }
  )
}
function renderSearch(h) {
  return h(
    'div',
    {
      class: 'tree-header__search'
    },
    [
      renderSearchField.call(this, h),
      renderImportButton.call(this, h)
    ]
  )
}
function renderHeader(h) {
  return h(
    'div',
    {
      class: 'tree-header'
    },
    [ renderSearch.call(this, h) ]
  )
}

function renderTab(h, tab) {
  return h(
    button,
    {
      props: {
        mode: buttonModes.flat,
        size: sizes.small,
        color: this.getTabColor(tab),
        label: this.getTranslate(`${Services.operatorGroups}.pills.${tab}`)
      },
      on: {
        click: () => {
          this.tab = tab
        }
      }
    }
  )
}
function renderTabs(h) {
  return h(
    'div',
    {
      class: 'tree-body__tabs'
    },
    [ 'all', 'checked' ].map(tab => {
      return renderTab.call(this, h, tab)
    })
  )
}
function renderAnalytics(h) {
  return h(
    tag,
    {
      props: {
        label: this.analyticsLabel,
        loading: this.loading.analytics,
        color: Colors.grey,
        size: sizes.medium
      }
    }
  )
}

function renderDescription(h, value, tooltip) {
  return h(
    'div',
    {
      class: 'tree__description',
      directives: [
        {
          name: 'g-tooltip',
          value: tooltip,
          options: {
            placement: 'top'
          }
        }
      ]
    },
    [ value ]
  )
}
function renderTitle(h, value) {
  return h(
    overflow,
    {
      class: 'tree__title',
      props: {
        value
      }
    }
  )
}
function renderSpacer(h) {
  return h(
    'div',
    {
      class: 'tree__spacer'
    }
  )
}
function renderHint(h, value, expandable) {
  return h(
    'div',
    {
      class: {
        tree__hint: true,
        'tree__hint--expandable': expandable
      }
    },
    [ value ]
  )
}

function renderOperatorChecker(h, group, operator) {
  return h(
    'div',
    {
      class: {
        ['tree__checker']: true,
        ['tree__checker--checked']: this.isOperatorChecked(group.mcc, operator.mnc)
      }
    }
  )
}
function renderOperatorContent(h, operator) {
  return h(
    'div',
    {
      class: 'tree__content'
    },
    [
      renderDescription.call(this, h, operator.mnc, this.getTranslate(`${Services.operatorGroups}.hints.tree.mnc`)),
      renderTitle.call(this, h, operator.operator),
      renderSpacer.call(this, h),
      renderHint.call(this, h, this.getTranslate(`${Services.operatorGroups}.hints.tree.operator`))
    ]
  )
}
function renderOperatorRow(h, group, operator) {
  if (operator.operator) {
    const reference = `operator:${this.getGroupKey(group)}:${operator.mnc}`

    return h(
      'div',
      {
        class: 'tree__row',
        ref: reference,
        on: {
          click: () => {
            this.toggleOperatorSelection({
              group: group.group,
              mcc: group.mcc,
              mnc: operator.mnc
            })
            this.scrollTree(reference)
          }
        }
      },
      [
        renderOperatorChecker.call(this, h, group, operator),
        renderOperatorContent.call(this, h, operator)
      ]
    )
  }
}
function renderOperators(h, group) {
  const operators = this.getVisibleOperators(group)

  if (operators.length) {
    return h(
      'div',
      {
        class: 'tree__level tree__level--3'
      },
      [
        operators.map(operator => {
          return renderOperatorRow.call(this, h, group, operator)
        })
      ]
    )
  }
}

function renderGroupChecker(h, group, reference) {
  return h(
    'div',
    {
      class: {
        ['tree__checker']: true,
        ['tree__checker--checked']: this.isGroupChecked(group),
        ['tree__checker--partial']: this.isGroupPartial(group)
      },
      on: {
        click: () => {
          this.toggleGroupSelection(group)
          this.scrollTree(reference)
        }
      }
    }
  )
}
function renderGroupContent(h, group, reference) {
  const state = this.getGroupState(group)

  return h(
    'div',
    {
      class: {
        ['tree__content']: true,
        [`tree__content--${state}`]: true,
        ['tree__content--checked']: this.isGroupChecked(group)
      },
      on: {
        click: () => {
          this.toggleGroupState(group)
          this.scrollTree(reference)
        }
      }
    },
    [
      renderTitle.call(this, h, group.group),
      renderSpacer.call(this, h),
      renderHint.call(this, h, this.getGroupHint(group), true)
    ]
  )
}
function renderGroupRow(h, group) {
  const reference = `group:${this.getGroupKey(group)}`

  return h(
    'div',
    {
      class: 'tree__row',
      ref: reference
    },
    [
      renderGroupChecker.call(this, h, group, reference),
      renderGroupContent.call(this, h, group, reference)
    ]
  )
}
function renderGroup(h, group) {
  return h(
    'div',
    {
      class: 'tree__holder'
    },
    [
      renderGroupRow.call(this, h, group),
      renderOperators.call(this, h, group)
    ]
  )
}
function renderGroups(h, country) {
  const groups = this.getVisibleGroups(country)

  if (groups.length) {
    return h(
      'div',
      {
        class: 'tree__level tree__level--2'
      },
      [
        groups.map(group => {
          return renderGroup.call(this, h, group)
        })
      ]
    )
  }
}

function renderCountryChecker(h, country, reference) {
  return h(
    'div',
    {
      class: {
        ['tree__checker']: true,
        ['tree__checker--checked']: this.isCountryChecked(country.mcc),
        ['tree__checker--partial']: this.isCountryPartial(country.mcc)
      },
      on: {
        click: () => {
          this.toggleCountrySelection(country)
          this.scrollTree(reference)
        }
      }
    }
  )
}
function renderCountryContent(h, country, reference) {
  const state = this.getCountryState(country)

  return h(
    'div',
    {
      class: {
        ['tree__content']: true,
        [`tree__content--${state}`]: true,
        ['tree__content--checked']: this.isCountryChecked(country.mcc)
      },
      on: {
        click: async () => {
          await this.toggleCountryState(country)
          this.scrollTree(reference)
        }
      }
    },
    [
      renderDescription.call(this, h, country.mcc, this.getTranslate(`${Services.operatorGroups}.hints.tree.mcc`)),
      renderTitle.call(this, h, country.country),
      renderSpacer.call(this, h),
      renderHint.call(this, h, this.getCountryHint(country), true)
    ]
  )
}
function renderCountryRow(h, country) {
  const reference = `country:${this.getCountryKey(country)}`

  return h(
    'div',
    {
      class: 'tree__row',
      ref: reference
    },
    [
      renderCountryChecker.call(this, h, country, reference),
      renderCountryContent.call(this, h, country, reference)
    ]
  )
}
function renderCountry(h, country) {
  return h(
    'div',
    {
      class: 'tree__holder'
    },
    [
      renderCountryRow.call(this, h, country),
      renderGroups.call(this, h, country)
    ]
  )
}
function renderCountries(h) {
  if (Array.isArray(this.visibleCountries) && this.visibleCountries.length) {
    return h(
      'div',
      {
        class: 'tree__level tree__level--1',
        ref: 'scrollableElement'
      },
      [
        this.visibleCountries.map(country => {
          return renderCountry.call(this, h, country)
        })
      ]
    )
  }
}

function renderEmpty(h) {
  return h(
    'div',
    {
      class: 'tree__empty'
    },
    [ h(components.empty) ]
  )
}

function renderBody(h) {
  return h(
    'div',
    {
      class: 'tree-body'
    },
    [
      renderTabs.call(this, h),
      renderAnalytics.call(this, h),
      renderCountries.call(this, h) ?? renderEmpty.call(this, h)
    ]
  )
}

export default function(h) {
  if (this.loading.dictionary) {
    return renderPreloader.call(this, h)
  }
  if (!this.dictionaryId) {
    return renderError.call(this, h)
  }

  return h(
    'div',
    {
      class: 'tree'
    },
    [
      renderHeader.call(this, h),
      renderBody.call(this, h)
    ]
  )
}
